import output from "_src/engine/output";
import {
  ColorTextureMaterial,
  MatCapMaterial,
  SurfelsMaterial,
  PBRMaterial,
  CADMaterial,
  CADEdgesMaterial
} from "_src/client/materials/material";

const _astudioMaterialStorage = new WeakMap();
const _matCapStorage = new Map();
const _pbrMaterialStorage = new WeakMap();

const ASTUDIO       = 0;
const MATCAP        = 1;
const SURFELS       = 2;
const CAD           = 3;
const CAD_EDGES     = 5;
const PBR           = 6;
const NONE          = 7;

export default {
  ASTUDIO,
  MATCAP,
  SURFELS,
  CAD,
  CAD_EDGES,
  NONE,
  PBR,

  async resetMaterial(type, model, properties) {
    _releaseMaterial(model);
    await _setMaterial(model, type, properties);
  },

  isAppliable(type, model) {
    switch (type) {
      case PBR:
        return _pbrMaterialStorage.has(model);
      default:
        return true;
    }
  }
}

function _releaseMaterial(model) {
  if (!model.properties) {
    return;
  }

  switch (model.properties.type) {
    case ASTUDIO: {
      const material = _astudioMaterialStorage.get(model);
      if (!material) {
        output.error(output.DEVELOPERS_ERROR, "Trying to release unregistered astudio material");
        return;
      }

      material.shader = model.shader;
      material.properties = model.properties;
      material.textures = model.textures;

      model.shader = null;
      model.properties = null;
      model.textures = null;

      break;
    }

    case PBR: {
      const material = _pbrMaterialStorage.get(model);
      if (!material) {
        output.error(output.DEVELOPERS_ERROR, "Trying to release unregistered pbr material");
        return;
      }

      let astudioMaterial = _astudioMaterialStorage.get(model);
      if (!astudioMaterial) {
        astudioMaterial = new ColorTextureMaterial(model.properties);
        _astudioMaterialStorage.set(model, astudioMaterial);
      }

      material.shader = model.shader;
      material.properties = model.properties;
      material.textures = [model.textures[1]];
      astudioMaterial.textures = [model.textures[0]];

      model.shader = null;
      model.properties = null;
      model.textures = null;

      break;
    }

    case MATCAP: {
      const materialEntry = _matCapStorage.get(model.properties.matCapPath);
      if (!materialEntry) {
        output.error(output.DEVELOPERS_ERROR, "Trying to release released matcap material");
        return;
      }

      materialEntry.ownersCounter--;
      if (materialEntry.ownersCounter === 0) {
        _matCapStorage.delete(model.properties.matCapPath);
      }

      break;
    }
  }
}

async function _setMaterial(model, type, properties) {
  properties.type = type;
  switch (type) {
    case ASTUDIO: {
      let material = _astudioMaterialStorage.get(model);
      if (!material) {
        material = new ColorTextureMaterial(properties);
        _astudioMaterialStorage.set(model, material);
      }

      model.shader = material.shader;
      model.properties = material.properties;
      model.textures = material.textures;

      material.shader = null;
      material.properties = null;
      material.textures = null;
      break;
    }

    case MATCAP: {
      let materialEntry = _matCapStorage.get(properties.matCapPath);
      if (!materialEntry) {
        materialEntry = {
          material: new MatCapMaterial(properties),
          ownersCounter: 0,
        };
        _matCapStorage.set(properties.matCapPath, materialEntry);
      }
      materialEntry.ownersCounter++;
      const material = materialEntry.material;

      await material.initPromise;

      model.shader = material.shader;
      model.properties = material.properties;
      model.textures = material.textures;

      break;
    }
    case PBR: {
      let material = _pbrMaterialStorage.get(model);
      if (!material) {
        material = new PBRMaterial(properties);
        _pbrMaterialStorage.set(model, material);
      }

      model.shader = material.shader;
      model.properties = material.properties;

      if (!material.resources) await material.initPromise;
      const resources = material.resources;
      const astudioMaterial = _astudioMaterialStorage.get(model);

      model.textures = [];
      if (astudioMaterial) model.textures[0] = astudioMaterial.textures[0];
      model.textures[1] = material.textures[0];
      model.textures[2] = resources.irradiance;
      model.textures[3] = resources.prefilter;
      model.textures[4] = resources.brdf;

      material.shader = null;
      material.properties = null;
      material.textures = null;

      break;
    }

    case SURFELS: {
      const material = new SurfelsMaterial(properties);

      model.shader = material.shader;
      model.properties = material.properties;
      model.textures = material.textures;

      break;
    }

    case CAD: {
      const material = new CADMaterial(properties);

      model.shader = material.shader;
      model.properties = material.properties;
      model.textures = material.textures;

      break;
    }

    case CAD_EDGES: {
      const EDGE_COLOR = [0.0, 0.0, 0.0];
      const material = new CADEdgesMaterial(properties);

      model.shader = material.shader;
      model.properties = material.properties;
      model.textures = material.textures;
      model.setDiffuseColor(...EDGE_COLOR);
      model.setFrustrumCulling(false);

      break;
    }

    case NONE: {
      model.shader = null;
      model.properties = null;
      model.textures = null;
      break;
    }

    default: {
      output.error(output.DEVELOPERS_ERROR, "can't get material with unknown type: " + type);
      return;
    }
  }
}
